import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import "./themeFonts.css";
import { isThemeId, themeDefinition, type ThemeId } from "./themeCatalog";
import { applyThemeToDocument, availableThemeDefinitions, parseThemeAvailability, resolveThemeId, type ThemeAvailability } from "./themeRuntime";
import { frozenThemeTokens } from "./themeTokens.generated";
import { readEmbedRequest } from "../onboarding/embedMode";
import { AppThemeContext, type AppThemeContextValue } from "./themeContext";

export type { AppThemeContextValue } from "./themeContext";

function readStoredThemeId(storageKey: string): ThemeId | null {
  try {
    const stored = typeof localStorage !== "undefined" ? localStorage.getItem(storageKey) : null;
    return isThemeId(stored) ? stored : null;
  } catch {
    return null;
  }
}

function writeStoredThemeId(storageKey: string, id: ThemeId | null) {
  try {
    if (id) localStorage.setItem(storageKey, id);
    else localStorage.removeItem(storageKey);
  } catch {
    // Ohne Speicher gilt die Wahl bis zum Neuladen.
  }
}

export type ThemeProviderProps = {
  children: React.ReactNode;
  /** Standard des Produkts, solange keine Freigabe geladen ist. */
  productDefaultThemeId: ThemeId;
  /** Gerätelokale Ablage der Benutzerwahl, z. B. je Konto. */
  storageKey: string;
  /** Liest einmalig eine frühere Auswahl, falls unter `storageKey` noch nichts liegt. */
  readLegacyThemeId?: () => ThemeId | null;
  /** Serverseitige Speicherung der Benutzerwahl, falls das Produkt sie hat. */
  onUserThemeChange?: (id: ThemeId) => void;
  /** Erzwingt ein Theme (z. B. öffentliche Widgets); Auswahl und Vorgaben bleiben erhalten. */
  forcedThemeId?: ThemeId | null;
};

/**
 * Gemeinsame Theme-Laufzeit aller Produkte: wählt das wirksame Theme aus Benutzerwahl, Mandanten-
 * und App-Standard, setzt Merkmale und `--mq-theme-*`-Variablen am Dokument und liefert die Antd-Konfiguration.
 */
export function ThemeProvider({ children, productDefaultThemeId, storageKey, readLegacyThemeId, onUserThemeChange, forcedThemeId }: ThemeProviderProps) {
  const [stored, setStored] = useState<{ key: string; id: ThemeId | null }>(() => ({ key: storageKey, id: readStoredThemeId(storageKey) ?? readLegacyThemeId?.() ?? null }));
  const [availability, setAvailability] = useState<ThemeAvailability | null>(null);
  const [previewThemeId, setPreviewThemeId] = useState<ThemeId | null>(null);
  // Eingebettete öffentliche Seiten tragen das Theme der einbettenden Seite in der Adresse; nichts davon wird gespeichert.
  const [embedThemeId] = useState(() => readEmbedRequest().themeId);

  // Wechselt die Ablage (z. B. anderes Konto), gilt deren Wahl ab dem nächsten Render.
  const userThemeId = stored.key === storageKey ? stored.id : readStoredThemeId(storageKey) ?? readLegacyThemeId?.() ?? null;
  if (stored.key !== storageKey) setStored({ key: storageKey, id: userThemeId });

  const resolvedId = resolveThemeId({ userThemeId, availability, productDefaultThemeId });
  const themeId = forcedThemeId ?? embedThemeId ?? previewThemeId ?? resolvedId;
  const theme = themeDefinition(themeId);

  useLayoutEffect(() => {
    applyThemeToDocument(theme);
  }, [theme]);

  const persistRef = useRef<((id: ThemeId) => void) | null>(null);
  const registerUserThemePersistence = useCallback((persist: ((id: ThemeId) => void) | null) => {
    persistRef.current = persist;
  }, []);

  const setThemeId = useCallback((id: ThemeId) => {
    writeStoredThemeId(storageKey, id);
    setStored({ key: storageKey, id });
    setPreviewThemeId(null);
    onUserThemeChange?.(id);
    persistRef.current?.(id);
  }, [onUserThemeChange, storageKey]);

  const adoptUserThemeId = useCallback((id: ThemeId | null) => {
    writeStoredThemeId(storageKey, id);
    setStored({ key: storageKey, id });
  }, [storageKey]);

  const loaderRef = useRef<(() => Promise<unknown>) | null>(null);
  const registerAvailabilityLoader = useCallback((load: (() => Promise<unknown>) | null) => {
    loaderRef.current = load;
  }, []);
  const reloadAvailability = useCallback(async () => {
    const load = loaderRef.current;
    if (!load) return;
    try {
      const next = parseThemeAvailability(await load());
      if (loaderRef.current === load) setAvailability(next);
    } catch {
      // Ohne Vorgaben vom Server gilt der Produktstandard; die Oberfläche bleibt bedienbar.
    }
  }, []);

  const value = useMemo<AppThemeContextValue>(() => ({
    themeId,
    theme,
    token: frozenThemeTokens[theme.id],
    fieldVariant: theme.fieldVariant,
    availableThemes: availableThemeDefinitions(availability),
    availability,
    userThemeId,
    setThemeId,
    adoptUserThemeId,
    setAvailability,
    reloadAvailability,
    registerAvailabilityLoader,
    registerUserThemePersistence,
    previewThemeId,
    setPreviewThemeId,
  }), [adoptUserThemeId, availability, previewThemeId, registerAvailabilityLoader, registerUserThemePersistence, reloadAvailability, setThemeId, theme, themeId, userThemeId]);

  return <AppThemeContext.Provider value={value}>{children}</AppThemeContext.Provider>;
}

